<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-svelte';
  import { adminCustomFieldsApi, adminVerticalsApi } from '$lib/api/admin';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { CustomFieldDef, Entity, FieldType } from '$lib/types';

  const queryClient = useQueryClient();

  const entities: Array<{ value: Entity; label: string }> = [
    { value: 'companies', label: 'Companies' },
    { value: 'people', label: 'People' },
    { value: 'deals', label: 'Deals' },
    { value: 'activities', label: 'Touchpoints' }
  ];
  const fieldTypes: FieldType[] = ['text', 'number', 'date', 'select', 'multiselect', 'checkbox'];

  let verticalId = $state<number | undefined>(undefined);
  let entity = $state<Entity>('companies');
  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');
  let newOption = $state('');

  function emptyForm() {
    return { key: '', label: '', type: 'text' as FieldType, options: [] as string[] };
  }
  let form = $state(emptyForm());

  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));

  $effect(() => {
    if (verticalId === undefined && verticalsQuery.data?.results.length) {
      verticalId = verticalsQuery.data.results[0].id;
    }
  });

  const fieldsQuery = createQuery(() => ({
    queryKey: ['admin-custom-fields', { verticalId, entity }],
    queryFn: () => adminCustomFieldsApi.list({ vertical_id: verticalId, entity }),
    enabled: verticalId !== undefined
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminCustomFieldsApi.update(editingId, body) : adminCustomFieldsApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-custom-fields'] });
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminCustomFieldsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-custom-fields'] })
  }));

  const reorderMutation = createMutation(() => ({
    mutationFn: (updates: Array<{ id: number; sort_order: number }>) =>
      Promise.all(updates.map((u) => adminCustomFieldsApi.update(u.id, { sort_order: u.sort_order }))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-custom-fields'] })
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    showForm = true;
  }

  function openEdit(row: CustomFieldDef) {
    editingId = row.id;
    form = { key: row.key, label: row.label, type: row.type, options: [...row.options] };
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
  }

  function addOption() {
    const v = newOption.trim();
    if (v && !form.options.includes(v)) form.options.push(v);
    newOption = '';
  }

  function removeOption(v: string) {
    form.options = form.options.filter((o) => o !== v);
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.key.trim() || !form.label.trim()) {
      formError = 'Key and label are required';
      return;
    }
    formError = '';
    if (editingId) {
      saveMutation.mutate({ label: form.label, type: form.type, options: form.options });
    } else {
      saveMutation.mutate({
        vertical_id: verticalId,
        entity,
        key: form.key,
        label: form.label,
        type: form.type,
        options: form.options,
        sort_order: (fieldsQuery.data?.results.length ?? 0) * 10
      });
    }
  }

  function remove(row: CustomFieldDef) {
    scheduleUndo(`Delete field "${row.label}"`, () => deleteMutation.mutate(row.id));
  }

  function move(row: CustomFieldDef, direction: -1 | 1) {
    const rows = fieldsQuery.data?.results ?? [];
    const idx = rows.findIndex((r) => r.id === row.id);
    const swapIdx = idx + direction;
    if (idx < 0 || swapIdx < 0 || swapIdx >= rows.length) return;
    const other = rows[swapIdx];
    reorderMutation.mutate([
      { id: row.id, sort_order: other.sort_order },
      { id: other.id, sort_order: row.sort_order }
    ]);
  }
</script>

<div>
  <div class="mb-[18px]">
    <h2 class="tym-section-title">Custom fields</h2>
    <div class="tym-section-sub">
      {verticalsQuery.data?.results.find((v) => v.id === verticalId)?.name ?? '—'} &middot;
      {entities.find((e) => e.value === entity)?.label} entity
    </div>
  </div>

  <div class="mb-4 flex flex-wrap items-center gap-3">
    <select class="input max-w-[220px]" bind:value={verticalId}>
      {#each verticalsQuery.data?.results ?? [] as v (v.id)}
        <option value={v.id}>{v.name}</option>
      {/each}
    </select>
    <div class="flex flex-wrap gap-1.5">
      {#each entities as e (e.value)}
        <button type="button" class="tym-chip {entity === e.value ? 'active' : ''}" onclick={() => (entity = e.value)}>
          {e.label}
        </button>
      {/each}
    </div>
    <span class="flex-1"></span>
    <button type="button" class="btn-primary" onclick={openCreate}>
      <Plus class="h-4 w-4" /> New field
    </button>
  </div>

  {#if fieldsQuery.isLoading}
    <p class="tym-loading-state">Loading fields&hellip;</p>
  {:else if fieldsQuery.isError}
    <p class="tym-error-state">{getErrorMessage(fieldsQuery.error)}</p>
  {:else if fieldsQuery.data}
    <div class="tym-table-wrap max-w-[640px]">
      <div class="tym-table-scroll">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Type</th>
              <th class="num"></th>
            </tr>
          </thead>
          <tbody>
            {#each fieldsQuery.data.results as row, i (row.id)}
              <tr class="hover:bg-toyama-light-carbon">
                <td class="cursor-pointer font-semibold text-navy" onclick={() => openEdit(row)}>
                  {row.label}
                  <div class="text-[11px] font-normal text-dark-gray">{row.key}</div>
                </td>
                <td>
                  <span class="tym-badge" style="background:#F1F0EF; color:#666">{row.type}</span>
                  {#if row.options.length}
                    <span class="ml-1.5 text-[11.5px] text-dark-gray">{row.options.join(', ')}</span>
                  {/if}
                </td>
                <td class="num">
                  <div class="flex justify-end gap-1.5">
                    <button
                      type="button"
                      class="btn-secondary !h-10 !w-10 !p-0 disabled:opacity-30"
                      disabled={i === 0}
                      onclick={() => move(row, -1)}
                      aria-label="Move up"
                    >
                      <ArrowUp class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="btn-secondary !h-10 !w-10 !p-0 disabled:opacity-30"
                      disabled={i === fieldsQuery.data.results.length - 1}
                      onclick={() => move(row, 1)}
                      aria-label="Move down"
                    >
                      <ArrowDown class="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      class="btn-danger !h-10 !w-10 !p-0"
                      onclick={() => remove(row)}
                      aria-label="Delete"
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            {:else}
              <tr><td class="empty" colspan="3">No custom fields yet.</td></tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>

{#if showForm}
  <Modal title={editingId ? 'Edit field' : 'New field'} onClose={closeForm}>
    <form class="space-y-4" onsubmit={submit}>
      <div>
        <label class="label" for="key">Key</label>
        <input id="key" class="input font-mono" bind:value={form.key} disabled={!!editingId} />
      </div>
      <div>
        <label class="label" for="label">Label</label>
        <input id="label" class="input" bind:value={form.label} />
      </div>
      <div>
        <label class="label" for="type">Type</label>
        <select id="type" class="input" bind:value={form.type}>
          {#each fieldTypes as t (t)}
            <option value={t}>{t}</option>
          {/each}
        </select>
      </div>
      {#if form.type === 'select' || form.type === 'multiselect'}
        <div>
          <span class="label">Options</span>
          <div class="flex flex-wrap gap-2 mb-2">
            {#each form.options as o (o)}
              <span class="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">
                {o}
                <button type="button" onclick={() => removeOption(o)} aria-label={`Remove ${o}`}>
                  <X class="h-3 w-3" />
                </button>
              </span>
            {/each}
          </div>
          <div class="flex gap-2">
            <input
              class="input"
              placeholder="Add an option"
              bind:value={newOption}
              onkeydown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addOption();
                }
              }}
            />
            <button type="button" class="btn-secondary" onclick={addOption}>Add</button>
          </div>
        </div>
      {/if}

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
